/**
 * Handles site header interactions, including dropdown menus, keyboard navigation,
 * hover events, scroll handling, and lazy loading images within dropdowns.
 */
export class SiteHeader {

    constructor() {

        /** @type {Object} Stores CSS selectors used throughout the class. */
        this.selectors = {
            classes: {
                headerSection: 'header-section',
                topLevelLinkItem: 'top-level-link-item',
                topLevelLink: 'top-level-link',
                menuDropdown: 'site-header__menu-dropdown',
                dropdownLink: 'dropdown-link',
                predictiveSearch: 'predictive-search-section',
                dropdownLoaded: 'menu-dropdown--loaded',
                stickyHeader: 'sticky-header-container',
            }
        };

        /** @type {HTMLElement} The main header section element. */
        this.headerSection = document.querySelector(`.${ this.selectors.classes.headerSection }`);

        /** @type {HTMLElement} The predictive search section element. */
        this.predictiveSearch = document.querySelector(`.${ this.selectors.classes.predictiveSearch }`);

        /** @type {HTMLElement} The sticky header section element. */
        this.stickyHeader = document.querySelector(`.${ this.selectors.classes.stickyHeader }`);

        this.init();
    }



    /**
     * Initializes header event handlers and checks dropdown states on load.
     */
    init() {
        if (this.headerSection) {
            this.handleKeyboardNavigation();
            this.handleHoverEvents();
            // this.handleClickEvents();
            this.setHeaderProperties();

            // document.body.addEventListener('click', this.handleClickEvent.bind(this) );
            this.lastScrollTop = window.scrollY; // Store the last scroll position
            this.handleScrollEvent = this.handleScrollEvent.bind(this);
            window.addEventListener('scroll', this.handleScrollEvent.bind(this) );
            document.addEventListener('DOMContentLoaded', this.checkDropdownStatesOnLoad.bind(this) );
        }
    }



    /**
     * Handles keyboard navigation for dropdown menus.
     */
    handleKeyboardNavigation() {
        this.headerSection.addEventListener('keydown', (e) => {
            const activeElement = document.activeElement;
            if (activeElement.classList.contains(this.selectors.classes.topLevelLink)) {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    this.toggleDropdown(activeElement, true);
                    const firstDropdownItem = activeElement.nextElementSibling?.querySelector(`.${this.selectors.classes.dropdownLink}`);
                    if (firstDropdownItem) firstDropdownItem.focus();

                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    const parentTopLevelLink = activeElement.closest(`.${this.selectors.classes.menuDropdown}`).previousElementSibling;
                    this.closeAllDropdowns();
                    parentTopLevelLink.focus();
                }

            } else if (activeElement.classList.contains(this.selectors.classes.dropdownLink)) {
                if (e.key === 'Escape') {
                    e.preventDefault();
                    const parentTopLevelLink = activeElement.closest(`.${this.selectors.classes.menuDropdown}`).previousElementSibling;
                    this.closeAllDropdowns();
                    parentTopLevelLink.focus();
                }
            }
        });

        this.headerSection.addEventListener('focusin', (e) => {
            // console.log('🚀 FOCUSED ON ELEMENT: ', e.target )
            const activeElement = document.activeElement;
            if (activeElement.classList.contains(this.selectors.classes.topLevelLink)) {
                this.closeAllDropdowns();
            }
        });

    }



    /**
     * Handles hover events to open and close dropdowns.
     */
    handleHoverEvents() {
        const topLevelItems = this.headerSection.querySelectorAll(`.${this.selectors.classes.topLevelLinkItem}`);

        topLevelItems.forEach(item => {
            item.addEventListener('mouseenter', () => {
                const link = item.querySelector(`.${this.selectors.classes.topLevelLink}`);
                this.toggleDropdown(link, true);
            });

            item.addEventListener('mouseleave', () => {
                this.closeAllDropdowns();
            });
        });
    }



    /**
     * Handles scroll events to adjust header properties with a throttle delay.
     */
    handleScrollEvent(e) {
        const currentScrollTop = window.scrollY;

        if (this.stickyHeader) {
            if (currentScrollTop > this.lastScrollTop) {
                this.stickyHeader.style.setProperty('--announcement-bar-offset', '-40px');
            } else if (currentScrollTop < this.lastScrollTop) {
                this.stickyHeader.style.setProperty('--announcement-bar-offset', '0px');
            }
        }

        this.lastScrollTop = currentScrollTop; // Update the last scroll position

        const throttleDelay = 150;
        this.scrollTimeout;
        this.scrollTimeout = setTimeout(() => {
            clearTimeout( this.scrollTimeout );

            this.setHeaderProperties();
        }, throttleDelay );


    }



    /**
     * Sets custom CSS properties for header height.
     */
    setHeaderProperties() {
        const headerHeight = this.headerSection.getBoundingClientRect().bottom;

        this.headerSection.style.setProperty('--site-header-offset', `${ headerHeight }px` )
    }



    /**
     * Handles click events for dropdown interactions.
     */
    /*
    handleClickEvents(e) {
        this.headerSection.addEventListener('click', (e) => {
            const eventTarget = e.target;
            console.log('eventTarget: ', eventTarget)
            if ( eventTarget.classList.contains(this.selectors.classes.topLevelLink) ) {
                const topLevelLink = eventTarget;

                this.toggleDropdown(topLevelLink, true);
                console.log('click toggling dropdown..')

            }
        })
    }
    */



    /**
     * Toggles the visibility of a dropdown.
     * @param {HTMLElement} targetLink - The top-level link associated with the dropdown.
     * @param {boolean} isOpen - Whether the dropdown should be opened.
     */
    toggleDropdown(targetLink, isOpen) {
        const dropdown = targetLink.nextElementSibling;
        if (dropdown) {
            this.closeAllDropdowns();
            if (isOpen) {
                dropdown.style.display = 'block';
                targetLink.setAttribute('aria-expanded', 'true');
                dropdown.setAttribute('aria-hidden', 'false');

                // Close Predictive Search
                if ( this.predictiveSearch ) this.predictiveSearch.close();

                // First Time Opened Dropdown Events
                if ( !dropdown.classList.contains( this.selectors.classes.dropdownLoaded ) ) {
                    dropdown.classList.add( this.selectors.classes.dropdownLoaded );
                    this.initBrandSlider( dropdown );
                    this.loadDropdownImages( dropdown );
                }

            }
        }
    }



    /**
     * Closes all dropdown menus.
     */
    closeAllDropdowns() {
        this.headerSection.querySelectorAll(`.${this.selectors.classes.menuDropdown}`).forEach(dropdown => {
            dropdown.style.display = 'none';
        });
        this.headerSection.querySelectorAll(`.${this.selectors.classes.topLevelLink}`).forEach(link => {
            const dropdown = link.nextElementSibling
            link.setAttribute('aria-expanded', 'false');
            if ( dropdown ) dropdown.setAttribute('aria-hidden', 'true');
        });
    }



    /**
     * Checks the state of dropdowns on page load.
     * If a dropdown is already visible, it initializes its brand slider and loads images.
     */
    checkDropdownStatesOnLoad() {
        this.headerSection.querySelectorAll(`.${this.selectors.classes.menuDropdown}`).forEach(dropdown => {
            const computedStyle = window.getComputedStyle(dropdown);

            if (computedStyle.display !== 'none' && !dropdown.classList.contains( this.selectors.classes.dropdownLoaded )) {
                this.initBrandSlider( dropdown );
                this.loadDropdownImages( dropdown );
                dropdown.classList.add( this.selectors.classes.dropdownLoaded );
            }
        });
    }



    /**
     * Loads images inside a given dropdown by replacing `data-src` attributes with `src`.
     * @param {HTMLElement} dropdown - The dropdown element containing images to be loaded.
     */
    loadDropdownImages( dropdown ) {
        const dropdownImages = dropdown.querySelectorAll(`img[data-src]`);

        if ( !dropdownImages.length ) return;

        // console.log('✅ Loading Dropdown Images...', dropdownImages );
        dropdownImages.forEach( img => {
            this.loadImage( img );
        });

    }



    /**
     * Loads a single image by setting its `src` attribute from `data-src` and removing `data-src`.
     * @param {HTMLImageElement} img - The image element to be loaded.
     */
    loadImage( img ) {
        const dataSrc = img.dataset.src;

        if ( !dataSrc ) return;

        img.src = dataSrc;
        img.removeAttribute('data-src');
    }



    /**
     * Initializes a brand slider inside a dropdown if it hasn't been initialized yet.
     * @param {HTMLElement} dropdown - The dropdown element containing the brand slider.
     */
    initBrandSlider( dropdown ) {
        const brandSlider = dropdown.querySelector('.dropdown-brand-slider__swiper');
        if ( brandSlider && !brandSlider.classList.contains('swiper-initialized') ) {
            const sliderId = brandSlider.id;

            const brandSliderSwiper = new window.swiper( `#${ sliderId }` , {
                direction: 'horizontal',
                slidesPerView: 'auto',
                spaceBetween: 0,
                centerInsufficientSlides: true,
                spaceBetween: 20,
                loop: true,
                speed: 600,

                autoplay: {
                    delay: 5000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                },

                on: {
                    init: function () {
                        let fadeOptions = {
                            threshold: 0.85,
                        }
                        new window.SwiperFadeItemsObserver( `#${ sliderId }`, '.swiper-slide', '(min-width: 768px)', fadeOptions );
                    },
                    afterInit: function () {
                        this.el.classList.remove('swiper-loading');
                    }
                }

            });

        }
    }



}
